import {
  type PortalFeatureFlagRegistry,
  defineNonFrontendPortalFeatureFlag,
  definePortalFeatureFlag,
} from './featureFlagDefinition';

export const PortalFeatureFlags = {
  // Harness flags
  menuV3_enableQa: definePortalFeatureFlag('harness', 'menuV3_enableQa', 'boolean'),
  sampleHarnessFlag: definePortalFeatureFlag('harness', 'sampleHarnessFlag', 'boolean'),
  accessEntitlementsV2Page: definePortalFeatureFlag(
    'harness',
    'accessEntitlementsV2Page',
    'boolean'
  ),
  liveOverviewReportHomepage: definePortalFeatureFlag(
    'harness',
    'liveOverviewReportHomepage',
    'boolean'
  ),
  ordersReport_redirectHeatmap: definePortalFeatureFlag(
    'harness',
    'ordersReport_redirectHeatmap',
    'boolean'
  ),
  bankAccount_pendingVerificationAsAdditionalInfo: definePortalFeatureFlag(
    'harness',
    'bankAccount_pendingVerificationAsAdditionalInfo',
    'boolean'
  ),
  bankAccount_ibanLookupInlineError: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'bankAccount_ibanLookupInlineError',
    'boolean'
  ),
  phoneAgentCutover: definePortalFeatureFlag('harness', 'phoneAgentCutover', 'boolean'),
  promotedVoucher: definePortalFeatureFlag('harness', 'promotedVoucher', 'boolean'),
  shouldUseActiveAlphaSender: definePortalFeatureFlag(
    'harness',
    'shouldUseActiveAlphaSender',
    'boolean'
  ),

  // Org-keyed Harness flags (traffic type org_id, targetingKey = orgId).
  // See docs/005-org-keyed-feature-flag-evaluation-for-harness-split-segments.md.
  showMenuListingV3Org: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'showMenuListingV3Org',
    'boolean'
  ),
  menuV3_enableEmptyNameValidation: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_enableEmptyNameValidation',
    'boolean'
  ),
  menuV3_enableMobileMenuEditor: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_enableMobileMenuEditor',
    'boolean'
  ),
  menuV3_showCategoryItemScheduling: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_showCategoryItemScheduling',
    'boolean'
  ),
  menuV3_showModifierScheduling: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_showModifierScheduling',
    'boolean'
  ),
  forceSalesMenuOption: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'forceSalesMenuOption',
    'boolean'
  ),
  // Hides Flipdish-sourced ("Legacy") menus in the v3 listing for orgs whose menus RMS now owns.
  menuV3_hideLegacyMenus: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_hideLegacyMenus',
    'boolean'
  ),
  // Org-keyed. Off: flat Menu Editor. On: expandable Menu with Menu Editor and Menu Actions.
  menuV3_showMenuSectionNav: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'menuV3_showMenuSectionNav',
    'boolean'
  ),
  // CE-1263/CE-1370: GB bank account form improvements (Sort Code lookup, field locking,
  // failure error). Off by default; enable per org in Harness.
  bankAccount_gbFormImprovements: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'bankAccount_gbFormImprovements',
    'boolean'
  ),
  // CE-1314: validate bank accounts with business-details instead of finance. Off by default
  // (finance); enable per org in Harness, and turn off to switch back.
  bankAccount_validateWithBusinessDetails: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'bankAccount_validateWithBusinessDetails',
    'boolean'
  ),
  // Uber Eats and Deliveroo direct integration settings open the marketplace-integrations Manage page
  salesChannel_openMarketplaceManage: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'salesChannel_openMarketplaceManage',
    'boolean'
  ),
  flipdishAssistant: definePortalFeatureFlag('harness', 'flipdishAssistant', 'boolean'),
  aiMarketing: definePortalFeatureFlag('harness', 'aiMarketing', 'boolean'),
  // Shared with the phone agent MFE: when on, phone numbers are edited only on its Phone numbers page.
  phoneAgentNumbersPage: definePortalFeatureFlag('harness', 'phoneAgentNumbersPage', 'boolean'),
  // The new design-system shell (ApplicationHeader, SidePanel, CommandMenu) in place of TopNav.
  showNewApplicationHeader: definePortalFeatureFlag(
    'harness',
    'showNewApplicationHeader',
    'boolean'
  ),
  // Auth0 ID token cutover master switch (see doc/adr/0008-portal-auth0-id-token-cutover.md).
  // Off by default — flag off (or unreadable) keeps the legacy Zeus code-exchange +
  // FD-Authorization cookie flow byte-for-byte; when on, login uses auth0-spa-js PKCE and
  // API calls present the ID token directly.
  auth0IdTokenCutover: definePortalFeatureFlag('harness', 'auth0IdTokenCutover', 'boolean'),
  // CE-1356: when on, teammate role comes from AuthZ GET /auth/orgs/{orgId}/user/roles
  // instead of Zeus AppAccessLevel. Off by default keeps Zeus behaviour.
  auth_useAuthZForUserRoles: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'auth_useAuthZForUserRoles',
    'boolean'
  ),

  // For any NonFrontend Portal feature flag you define here, you must also add it to the NONFRONTEND_FEATURE_FLAG_NAMES array in the harnessProvider.ts file.
  backend_auth_useAuthZForGetApps: defineNonFrontendPortalFeatureFlag(
    'harness',
    'backend_auth_useAuthZForGetApps',
    'boolean'
  ),
  backend_menu_shouldAssignMenuToStoreFromOomEvents: defineNonFrontendPortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'backend_menu_shouldAssignMenuToStoreFromOomEvents',
    'boolean'
  ),
  // Org-keyed demo org allowlist (traffic type org_id). Used for production demo-org TopNav banner.
  demo_orgs: defineNonFrontendPortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'demo_orgs',
    'boolean'
  ),
  DOS_showThirdPartyDeliveryInSidebar: definePortalFeatureFlag(
    'harnessWithOrgIdTargetingKey',
    'DOS_showThirdPartyDeliveryInSidebar',
    'boolean'
  ),
} as const satisfies PortalFeatureFlagRegistry;
